import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Book, EntityType, StorySoFar } from "../api";
import { useAuth } from "../auth";
import { BookmarkRuler, useFlash } from "../bookmark";
import { ENTITY_TYPES, ErrorNote, FACT_TYPES, Spinner, relationLabel, useLoad } from "../ui";
import { useBookmark } from "../wiki/common";
import type { ChapterPage, Rich } from "../wiki/types";

/**
 * Quick lookup: the shortest path from "who was that again?" to an answer (time-to-context
 * under five seconds). One search field, a type filter and the bookmark; with an empty search
 * it shows what is new in the chapter just read. Everything comes from the same gated API as
 * the reading room, so nothing past the bookmark can appear here either.
 */
export function LookupPage() {
  const { bookId = "" } = useParams();
  const { api } = useAuth();
  const book = useLoad(() => api.book(bookId), [api, bookId]);
  if (book.error) return <ErrorNote error={book.error} />;
  if (!book.data) return <Spinner />;
  if (book.data.first_chapter_index === null || book.data.last_chapter_index === null)
    return <p className="muted">Opzoeken kan zodra het boek is verwerkt.</p>;
  return <Lookup book={book.data} />;
}

type TypeFilter = "all" | EntityType;

function Lookup({ book }: { book: Book }) {
  const { api } = useAuth();
  const [bookmark, setBookmark] = useBookmark(book);
  const first = book.first_chapter_index ?? 0;
  const last = book.last_chapter_index ?? 0;
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [type, setType] = useState<TypeFilter>("all");
  const [open, setOpen] = useState<number | null>(null);
  const [view, setView] = useState<"ahead" | "new">("ahead");
  const [gain, flash] = useFlash(2200);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 150);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => input.current?.focus(), []);

  // "/" jumps to the search field from anywhere on the page.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(tag)) {
        e.preventDefault();
        input.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useLoad(
    () => (debounced ? api.entities(book.id, bookmark, debounced) : Promise.resolve([])),
    [api, book.id, bookmark, debounced],
  );
  const rows = useMemo(
    () =>
      (results.data ?? [])
        .filter((e) => type === "all" || e.entity_type === type)
        .sort((a, b) => b.mention_count - a.mention_count || a.name.localeCompare(b.name)),
    [results.data, type],
  );
  const chapter = useLoad(
    () => api.wikiChapter(book.id, bookmark, bookmark),
    [api, book.id, bookmark],
  );
  const story = useLoad(() => api.story(book.id, bookmark), [api, book.id, bookmark]);

  function readNext() {
    if (bookmark >= last) return;
    setBookmark(bookmark + 1);
    setOpen(null);
    flash(`Boekenlegger op hoofdstuk ${bookmark + 1}`);
  }

  const searching = debounced.length > 0;
  return (
    <div className="lookup">
      <p className="lookup-book">
        <Link to={`/books/${book.id}`}>{book.title}</Link>
        <span className="muted"> · snel opzoeken</span>
      </p>
      <BookmarkRuler
        first={first}
        last={last}
        value={bookmark}
        onChange={(n) => {
          setBookmark(n);
          setOpen(null);
        }}
        current={chapter.data?.chapter.title}
        gain={gain}
      />

      <form
        className="lookup-search"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (rows[0]) setOpen(rows[0].entity_id);
        }}
      >
        <label className="sr-only" htmlFor="lookup-q">
          Zoek een persoon, plaats of voorwerp
        </label>
        <input
          id="lookup-q"
          ref={input}
          type="search"
          autoComplete="off"
          placeholder="Wie of wat zoek je?"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setQuery("");
              setOpen(null);
            }
          }}
        />
        <span className="lookup-count" aria-live="polite">
          {searching && results.data ? `${rows.length} gevonden` : ""}
        </span>
      </form>
      <div className="seg lookup-types" role="group" aria-label="Soort">
        <button type="button" aria-pressed={type === "all"} onClick={() => setType("all")}>
          Alles
        </button>
        {ENTITY_TYPES.map((t) => (
          <button key={t.type} type="button" aria-pressed={type === t.type} onClick={() => setType(t.type)}>
            {t.label}
          </button>
        ))}
      </div>

      {searching ? (
        <section className="lookup-results" aria-label="Resultaten">
          <ErrorNote error={results.error} />
          {results.data && rows.length === 0 && (
            <p className="muted">
              Geen bekende naam "{debounced}" tot en met hoofdstuk {bookmark}. Misschien komt die pas
              later, of heet het anders.
            </p>
          )}
          <ul className="plain">
            {rows.map((e) => (
              <li key={e.entity_id} className={open === e.entity_id ? "lookup-open" : ""}>
                <button
                  className="lookup-hit"
                  aria-expanded={open === e.entity_id}
                  onClick={() => setOpen(open === e.entity_id ? null : e.entity_id)}
                >
                  <span className="lookup-name">{e.name}</span>
                  <span className="muted small">
                    {typeLabel(e.entity_type)} · vanaf H{e.first_seen_chapter}
                    {e.aliases.length > 0 && ` · ook ${e.aliases.join(", ")}`}
                  </span>
                </button>
                {open === e.entity_id && (
                  <Detail bookId={book.id} entityId={e.entity_id} bookmark={bookmark} />
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <>
          <div className="seg lookup-view" role="tablist" aria-label="Weergave">
            <button role="tab" aria-pressed={view === "ahead"} aria-selected={view === "ahead"}
                    onClick={() => setView("ahead")}>
              Vooruitblik
            </button>
            <button role="tab" aria-pressed={view === "new"} aria-selected={view === "new"}
                    onClick={() => setView("new")}>
              Nieuw in hoofdstuk {bookmark}
            </button>
          </div>
          {view === "ahead" ? (
            <StoryAhead
              bookmark={bookmark}
              last={last}
              story={
                story.data && {
                  ...story.data,
                  entities: story.data.entities.filter((e) => type === "all" || e.entity_type === type),
                }
              }
              error={story.error}
              onPick={(name) => {
                setQuery(name);
                setDebounced(name);
              }}
              onNext={readNext}
            />
          ) : (
            <NewInChapter
              bookmark={bookmark}
              last={last}
              page={chapter.data}
              error={chapter.error}
              onPick={(name) => {
                setQuery(name);
                setDebounced(name);
              }}
              onNext={readNext}
            />
          )}
        </>
      )}
    </div>
  );
}

function typeLabel(t: EntityType): string {
  return { character: "Personage", place: "Plaats", object: "Voorwerp", group: "Groep" }[t];
}

function plain(r: Rich | null | undefined): string {
  return (r ?? []).map((s) => s.t).join("");
}

/**
 * "The story so far", aimed at the next chapter: who comes back in it and what the reader
 * already knows about them. The server picks the names; all content stops at the bookmark.
 */
function StoryAhead(props: {
  bookmark: number;
  last: number;
  story: StorySoFar | null;
  error: string | null;
  onPick: (name: string) => void;
  onNext: () => void;
}) {
  const { story, bookmark, last } = props;
  return (
    <section className="lookup-new" aria-label="Het verhaal tot nu toe">
      <div className="lookup-new-head">
        <h2>Het verhaal tot nu toe</h2>
        {bookmark < last && (
          <button className="btn btn-ghost small" onClick={props.onNext}>
            Hoofdstuk {bookmark + 1} gelezen →
          </button>
        )}
      </div>
      <p className="muted lookup-intro">
        Wie en wat je in het volgende hoofdstuk weer tegenkomt, en wat je daar tot en met hoofdstuk{" "}
        {bookmark} al over weet. Nieuwe namen en wat er straks gebeurt zie je hier niet.
      </p>
      <ErrorNote error={props.error} />
      {!story && !props.error && <Spinner />}
      {story && story.entities.length === 0 && (
        <p className="muted">
          {bookmark >= last
            ? "Je bent bij het laatste hoofdstuk."
            : "In het volgende hoofdstuk komt niemand terug die je al kent."}
        </p>
      )}
      {story && story.entities.length > 0 && (
        <ul className="plain lookup-story">
          {story.entities.map((e) => (
            <li key={e.entity_id}>
              <p className="lookup-story-head">
                <button className="link lookup-story-name" onClick={() => props.onPick(e.name)}>
                  {e.name}
                </button>
                <span className="muted small">
                  {typeLabel(e.entity_type)} · laatst in H{e.last_seen_chapter}
                  {bookmark - e.last_seen_chapter >= 5 && " · al even niet gezien"}
                </span>
              </p>
              {e.facts.length === 0 ? (
                <p className="muted small">Nog geen feiten bekend.</p>
              ) : (
                <ol className="facts">
                  {e.facts.map((f) => (
                    <li key={f.fact_id}>
                      <span className="chapter-tag">H{f.chapter_index}</span> {f.text}
                    </li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ul>
      )}
      {story && story.events.length > 0 && (
        <div className="lookup-block">
          <h3>Wat er speelde</h3>
          <ul className="plain">
            {story.events.map((ev) => (
              <li key={ev.event_id}>
                <span className="chapter-tag">H{ev.chapter_index}</span> <strong>{ev.title}</strong>{" "}
                <span className="muted">{ev.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function NewInChapter(props: {
  bookmark: number;
  last: number;
  page: ChapterPage | null;
  error: string | null;
  onPick: (name: string) => void;
  onNext: () => void;
}) {
  const { page, bookmark, last } = props;
  return (
    <section className="lookup-new" aria-label={`Nieuw in hoofdstuk ${bookmark}`}>
      <div className="lookup-new-head">
        <h2>Nieuw in hoofdstuk {bookmark}</h2>
        {bookmark < last ? (
          <button className="btn btn-ghost small" onClick={props.onNext}>
            Hoofdstuk {bookmark + 1} gelezen →
          </button>
        ) : (
          <span className="muted small">Laatste hoofdstuk</span>
        )}
      </div>
      {props.error && props.error !== "page not found" && <ErrorNote error={props.error} />}
      {!page && !props.error && <Spinner />}
      {page && (
        <>
          {page.introduced.length === 0 && page.events.length === 0 && page.facts.length === 0 && (
            <p className="muted">In dit hoofdstuk komt niets nieuws bij.</p>
          )}
          {page.introduced.length > 0 && (
            <div className="lookup-block">
              <h3>Voor het eerst genoemd</h3>
              <p className="lookup-names">
                {page.introduced.map((e) => (
                  <button key={e.entity_id} className="link" onClick={() => props.onPick(e.name)}>
                    {e.name}
                  </button>
                ))}
              </p>
            </div>
          )}
          {page.events.length > 0 && (
            <div className="lookup-block">
              <h3>Wat er gebeurt</h3>
              <ul className="plain">
                {page.events.map((ev) => (
                  <li key={ev.event_id}>
                    <strong>{ev.title}</strong> <span className="muted">{plain(ev.description)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {page.facts.length > 0 && (
            <div className="lookup-block">
              <h3>Wat we leren</h3>
              <ul className="plain">
                {page.facts.map((g) => (
                  <li key={g.entity_id}>
                    <button className="link" onClick={() => props.onPick(g.name)}>
                      {g.name}
                    </button>
                    : {g.items.map(plain).join(" · ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <p className="muted small lookup-hint">
        Typ een naam om iemand op te zoeken; <kbd>/</kbd> springt naar het zoekveld, <kbd>Enter</kbd>{" "}
        opent de eerste treffer.
      </p>
    </section>
  );
}

function Detail(props: { bookId: string; entityId: number; bookmark: number }) {
  const { api } = useAuth();
  const d = useLoad(
    () => api.entity(props.bookId, props.entityId, props.bookmark),
    [api, props.bookId, props.entityId, props.bookmark],
  );
  if (d.error) return <ErrorNote error={d.error} />;
  if (!d.data) return <Spinner />;
  const e = d.data;
  return (
    <div className="lookup-detail">
      {e.facts.length === 0 ? (
        <p className="muted">Nog geen feiten bekend.</p>
      ) : (
        <ol className="facts">
          {[...e.facts].reverse().map((f) => (
            <li key={f.fact_id}>
              <span className="chapter-tag">H{f.chapter_index}</span> {f.text}{" "}
              <span className="fact-type">{FACT_TYPES[f.fact_type] ?? f.fact_type}</span>
            </li>
          ))}
        </ol>
      )}
      {e.relations.length > 0 && (
        <p className="lookup-rels">
          {e.relations.map((r, i) => (
            <span key={r.relation_id}>
              {i > 0 && " · "}
              {relationLabel(r.relation_type, r.direction)}{" "}
              <strong>{r.other_name}</strong>
            </span>
          ))}
        </p>
      )}
      <p className="small">
        <Link to={`/books/${props.bookId}/wiki/a/${e.entity_id}?bm=${props.bookmark}`}>
          Volledig artikel in de wiki →
        </Link>
      </p>
    </div>
  );
}
